<template>
  <div id="googleMapContainer">
    <div
      class="col-12 d-flex align-items-center justify-content-center mapOverlay"
      v-if="this.loading"
    >
      <div class="d-flex flex-column">
        <Spinner></Spinner>
      </div>
    </div>

    <div
      class="col-12 d-flex align-items-center justify-content-center mapOverlay"
      v-else-if="this.markers.length === 0"
    >
      <div class="d-flex flex-column">
        <div class="text-center">
          <i class="fas fa-exclamation-circle fa-8x" style="color: white"></i>
          <p style="color: white">{{ $t("common.placeholders.noResults") }}</p>
        </div>
      </div>
    </div>

    <gmap-map
      ref="gmap"
      class="google-map m-0 mb-12"
      :center="{ lat: 46.0037, lng: 8.9511 }"
      :zoom="18"
      map-type-id="roadmap"
      style="height: 100%"
      :options="{
        panControl: true,
        zoomControl: true,
        mapTypeControl: true,
        scaleControl: true,
        streetViewControl: true,
        overviewMapControl: true,
        clickableIcons: false,
        styles: [
          {
            featureType: 'poi.business',
            stylers: [{ visibility: 'off' }],
          },
        ],
      }"
    >
      <gmap-cluster :renderer="renderer">
        <!--  Create the markers -->
        <gmap-marker
          v-for="(m, i) in markers"
          :key="i"
          :position="m.position"
          :clickable="true"
          @click="selectMarker(m)"
          :icon="customMarker"
        />
      </gmap-cluster>

      <!-- Create the info window -->
      <gmap-info-window
        class="gmap-info-window"
        v-if="currentMarker"
        :options="infoOptions"
        :position="currentMarker.position"
        :opened="currentMarker !== undefined"
      >
        <div id="gm-custom-info-container">
          <div class="gm-custom-card">
            <!-- @clickedImage="clickedCarousel(listing)" style="height: 320px; border-radius: 5px 5px 0 0;" -->
            <ImageCarousel
              class="gm-custom-card-img-container"
              v-if="markerImageUrls"
              :images="markerImageUrls"
            ></ImageCarousel>
            <div class="gm-custom-card-body">
              <h6 class="gm-custom-card-subtitle">
                {{ this.$i18n.t("common.keywords.listing") }}
                {{ this.currentMarker.infoWindow.objectId }}
              </h6>
              <h5 class="gm-custom-card-title">
                {{
                  this.$options.filters.localized(
                    this.currentMarker.infoWindow.name
                  )
                }}
              </h5>
            </div>
            <div class="gm-custom-card-button-container">
              <div class="gm-custom-card-column">
                <!-- /*closeWindow()*/ -->
                <button
                  class="btn gm-custom-card-button gm-custom-card-button-outline"
                  type="button"
                  @click="closeInfoWindow()"
                >
                  {{ this.$i18n.t("common.actions.close") }}
                </button>
              </div>
              <div class="gm-custom-card-column">
                <button
                  class="btn gm-custom-card-button"
                  type="button"
                  @click="visitListing()"
                >
                  {{ this.$i18n.t("common.actions.view") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </gmap-info-window>
    </gmap-map>
  </div>
</template>

<script>
import Spinner from "@/components/common/Spinner";
import { GmapMap, GmapMarker, GmapInfoWindow, components } from "gmap-vue";

import ImageCarousel from "../../components/common/ImageCarousel.vue";
export default {
  name: "MapContainer",
  components: {
    Spinner,
    "gmap-cluster": components.Cluster,
    ImageCarousel,
  },
  props: {
    coordinates: {
      required: true,
      default: function () {
        return undefined;
      },
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  watch: {
    markers: function (oldVal, newVal) {
      if (this.isUndefined(this.markers)) {
        return;
      }

      // Verify that the coordinates have actually changed
      const oldObjectIds = oldVal.map((el) => el.infoWindow.objectId).sort();
      const newObjectIds = newVal.map((el) => el.infoWindow.objectId).sort();

      let markersAreEqual =
        oldObjectIds.length == newObjectIds.length &&
        oldObjectIds.every((element, index) => element === newObjectIds[index]);

      if (markersAreEqual) {
        return;
      }

      this.$refs.gmap.$mapPromise.then((map) => {
        const bounds = new google.maps.LatLngBounds();

        for (let m of this.markers) {
          let c = { lat: m.position.lat, lng: m.position.lng };
          bounds.extend(c);
        }
        map.fitBounds(bounds);
      });
    },
  },
  methods: {
    panToCoordinate: function (coords) {
      if (this.isUndefined(coords)) {
        return;
      }
      this.$refs.gmap.$mapPromise.then((map) => {
        map.panTo(coords);
      });
    },
    selectMarker: function (marker) {
      if (this.currentMarker !== undefined) {
        this.currentMarker = undefined;
      }
      this.currentMarker = marker;
      this.fetchMarkerImages(marker);
    },
    closeInfoWindow: function () {
      console.log("Called");
      this.currentMarker = undefined;
    },
    visitListing: function () {
      const objectId = this.currentMarker.infoWindow.objectId;
      let routeData = this.$router.resolve(`/${objectId}`);
      window.open(routeData.href, "_blank");
    },
    fetchMarkerImages: function (marker) {
      this.markerImageUrls = [];
      const objectId = marker.infoWindow.objectId;
      this.$http(`/listings/${objectId}/markers`)
        .then((res) => {
          // We need to add the id to the payload, the image carousel expects it
          let counter = 0;
          this.markerImageUrls = res.data.staticFilenames.map((imageUrl) => {
            return {
              id: `${objectId}-${counter++}`,
              url: imageUrl,
            };
          });
        })
        .catch((err) => console.log(err));
    },
  },
  computed: {
    renderer: function () {
      return {
        render: ({ count, position }) => {
          const color = "#0673b2";
          const markerSize = 120;

          // create svg url with fill color
          const svg = window.btoa(`
          <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${
            markerSize * 2
          } ${markerSize * 2}">
            <circle cx="${markerSize}" cy="${markerSize}" opacity="1" r="${
            markerSize - 50
          }" />
            <circle cx="${markerSize}" cy="${markerSize}" opacity=".6" r="${
            markerSize - 30
          }" />
            <circle cx="${markerSize}" cy="${markerSize}" opacity=".4" r="${
            markerSize - 10
          }" />
            <circle cx="${markerSize}" cy="${markerSize}" opacity=".2" r="${
            markerSize + 10
          }" />
          </svg>`);

          // create marker using svg icon
          return new google.maps.Marker({
            position,
            icon: {
              url: `data:image/svg+xml;base64,${svg}`,
              scaledSize: new google.maps.Size(45, 45),
            },
            label: {
              text: String(count),
              color: "rgba(255,255,255,1)",
              fontSize: "12px",
            },
            // adjust zIndex to be above other markers
            zIndex: 1000 + count,
          });
        },
      };
    },
    markers: function () {
      if (this.isUndefined(this.coordinates)) {
        return [];
      }

      return this.coordinates
        .filter(
          (coordinate) =>
            coordinate.latitude !== null && coordinate.longitude !== null
        )
        .map((coordinate) => {
          return {
            position: {
              lat: coordinate.latitude,
              lng: coordinate.longitude,
            },
            infoWindow: {
              name: coordinate.name,
              objectId: coordinate.objectId,
              image: `${this.resolveMarkerImage(coordinate.id)}`,
            },
          };
        });
    },
    customMarker: function () {
      return `${process.env.BASE_URL}casafile_marker.png`;
    },
    infoOptions: function () {
      if (!this.currentMarker) {
        return {};
      }

      return {
        maxWidth: 200,
        maxHeight: 250,
        pixelOffset: {
          width: 0,
          height: -50,
        },
      };
    },
  },
  data() {
    return {
      markerImageUrls: undefined,
      currentMarker: undefined,
      shape: {
        coords: [10, 10, 10, 15, 15, 15, 15, 10],
        type: "poly",
      },
    };
  },
};
</script>

<style lang="scss">
.gmap-info-window {
  width: 200px;
  height: 250px;
}
#gm-custom-info-container {
  width: 200px !important;
  height: 250px;

  border-radius: 5px;
}

.gm-custom-card {
  height: 100%;

  &-body {
    padding: 5px;
  }

  &-img-top {
    height: 140px;
    margin: auto;
    width: auto;
  }
  &-title {
    margin: 0px;
    max-height: 36px;
    overflow: hidden;
    font-size: 13px;
  }

  &-subtitle {
    margin: 0;
    color: #999 !important;
    font-weight: 400;
    font-size: 13px;
  }

  &-button-container {
    display: flex;
    flex-direction: row;

    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
  }

  &-column {
    width: 50%;
    padding: 5px;
  }

  &-button {
    width: 100%;
    border-radius: 5px;
    color: white;

    &-outline {
      color: $primary-color;
      background-color: white !important;
      border: 1px solid $primary-color !important;
    }
  }
}

.gm-style-iw {
  width: 250px !important;
  border-radius: 5px;
}

.gm-style-iw-d > div {
  width: 200px !important;
}

.gm-style-iw.gm-style-iw-c {
  border-radius: 5px !important;
}

.gm-custom-card-img-container {
  padding: 0px !important;
  height: 130px !important;
}
</style>

<style lang="scss" scoped>
#googleMapContainer {
  width: 100%;
  height: 100%;
}
.mapOverlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
}
</style>
